import SearchParamsBase from '../../../Elements/SearchParamsBase';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import importListHelper from '../../../Helpers/importListHelper';
import AuthService from '../../../Services/authService';

export default class TpEvaluatorSearchParams extends SearchParamsBase {
  // Non-filter instance data excluded from the serialized request (see SearchParamsBase):
  // Has*Role are UI-permission flags used only in field hide/showCondition logic;
  // *Placeholder are TextReadOnly display mirrors shown when the real field is hidden.
  // Inherited by the TpEvaluator* subclasses (Persona/Scheduling/EmailCampaign/TextMessage),
  // which reuse a subset of these fields; deleting an absent key is a harmless no-op.
  fieldsToExcludeFromSerialization() {
    return [
      'HasEmailCampaignExportAdminRole',
      'HasEmailCampaignExportAdminEmailOptOutRole',
      'HasTextMessageExportAdminRole',
      'EmailOptOutPlaceholder',
      'EmailVerifiedPlaceholder',
      'IncludeNATextMessageAuthorizedPlaceholder',
      'IncludeSelfDeactivatedPlaceholder',
      'TextAllowPlaceholder',
    ];
  }

  initialize() {
    this.HasEmailCampaignExportAdminRole = AuthService.HasRole('Report_EmailCampaignExportAdmin');
    this.HasEmailCampaignExportAdminEmailOptOutRole = AuthService.HasRole(
      'Report_EmailCampaignExportAdmin_EmailOptOut'
    );
    this.HasTextMessageExportAdminRole = AuthService.HasRole('Report_EvaluatorTextMessageExportAdmin');
    this.Id = '';
    this.TpEvaluatorId = '';
    this.ThirdPartySources = [];
    this.FieldworkOfficeIds = [];
    this.ThirdPartyEvaluatorId = '';
    this.Identifier = '';
    this.Name = '';
    this.Email = '';
    this.AgeFrom = '';
    this.AgeTo = '';
    this.Status = '';
    this.ThirdPartyStatus = '';
    this.DeactivationType = '';
    this.AverageRatingFrom = '';
    this.AverageRatingTo = '';
    this.LastRatingFrom = '';
    this.LastRatingTo = '';
    this.UseSignUpDateRange = false;
    this.SignupDateTimeUtcFrom = dateTimeHelper.indefiniteDateValue();
    this.SignupDateTimeUtcTo = dateTimeHelper.indefiniteDateValue();
    this.ExcludeCountry = false;
    this.SelectedCountries = [];
    this.States = '';
    this.ZipCodes = '';
    this.Cities = '';
    this.Agency = false;
    this.SelectedGender = '';
    this.ExcludeCitizenship = false;
    this.SelectedCitizenships = [];
    this.SelectedBusinesses = [];
    this.SelectedTipaltiInvitedStatuses = [];
    this.SelectedRatings = [];
    this.EvaluatorExtendedEvexRegBusiness = [];

    this.LocationSearchRadius = '';
    this.LocationSearchRadiusMeasurementUnit = 'kilometers';

    this.additionalInitialization();
  }

  additionalInitialization() {
    //can be overridden in derived classes
    this.TypeOfExport = null;
    this.Status = null;
    this.EmailOptOut = null;
    this.EmailOptOutPlaceholder = null;
    this.EmailVerified = null;
    this.EmailVerifiedPlaceholder = null;
    this.IncludeSelfDeactivated = null;
    this.IncludeSelfDeactivatedPlaceholder = null;
    this.TextAllow = null;
    this.TextAllowPlaceholder = null;
    this.IncludeNATextMessageAuthorized = null;
    this.IncludeNATextMessageAuthorizedPlaceholder = null;
  }

  static tabsOnTheSearchPanel = [
    { label: 'Base', id: 'base', icon: 'fa-solid fa-baseball' },
    { label: 'Personal', id: 'personal', icon: 'fa-solid fa-person' },
    { label: 'Location', id: 'location', icon: 'fa-solid fa-globe' },
    { label: 'Properties', id: 'properties', icon: 'fa-solid fa-car' },
    { label: 'Rating', id: 'rating', icon: 'fa-solid fa-star' },
    { label: 'Other', id: 'other', icon: 'fa-solid fa-clothes-hanger' },
  ];
  static fieldsInSearchBox = [
    //------Base tab
    {
      fieldName: 'TpEvaluatorId',
      label: 'Bravo Evaluator IDs',
      className: 'col-3',
      type: InputTypesEnum.TextList,
      tooltipText: importListHelper.ListFieldTooltip,
      tab: 'base',
    },
    {
      fieldName: 'ThirdPartySources',
      label: 'Third Party Sources',
      className: 'col-3',
      type: InputTypesEnum.ThirdPartySourceEvaluatorMultiChoice,
      tab: 'base',
    },
    {
      fieldName: 'FieldworkOfficeIds',
      label: 'Fieldwork Offices',
      className: 'col-3',
      type: InputTypesEnum.OfficeOrGroupMultiSelect,
      roleKey: 'evaulator_inputfield_EvaluatorGlobalView',
      tab: 'base',
    },
    {
      fieldName: 'ThirdPartyEvaluatorId',
      label: 'Third Party Evaluator IDs',
      className: 'col-3',
      type: InputTypesEnum.TextList,
      tooltipText: importListHelper.ListFieldTooltip,
      tab: 'base',
    },
    {
      fieldName: 'Identifier', //Renamed from 'Login as it causes 403 error responses
      label: 'Login',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'base',
    },
    {
      fieldName: 'Status',
      label: 'Status',
      className: 'col-3',
      type: InputTypesEnum.Select,
      options: TpEvaluatorSearchParams.getSelectOptionsForStatus(),
      tab: 'base',
      hide: (searchItem) =>
        searchItem.TypeOfExport === 'EmailCampaignExport' || searchItem.TypeOfExport === 'TextMessageExport',
    },
    {
      fieldName: 'Status',
      label: 'Status',
      className: 'col-3',
      type: InputTypesEnum.TextReadOnly,
      tab: 'base',
      hide: (searchItem) =>
        searchItem.TypeOfExport !== 'EmailCampaignExport' && searchItem.TypeOfExport !== 'TextMessageExport',
    },
    {
      fieldName: 'ThirdPartyStatus',
      label: 'Third Party Status',
      className: 'col-3',
      type: InputTypesEnum.Select,
      options: TpEvaluatorSearchParams.getSelectOptionsForThirdPartyStatus(),
      tab: 'base',
      hide: (searchItem) =>
        searchItem.TypeOfExport === 'EmailCampaignExport' || searchItem.TypeOfExport === 'TextMessageExport',
    },
    {
      fieldName: 'DeactivationType',
      label: 'Deactivation Type',
      className: 'col-3',
      type: InputTypesEnum.Select,
      options: TpEvaluatorSearchParams.getSelectOptionsForDeactivationType(),
      tab: 'base',
      hide: (searchItem) =>
        searchItem.TypeOfExport === 'EmailCampaignExport' || searchItem.TypeOfExport === 'TextMessageExport',
    },
    {
      fieldName: 'LastNDays',
      label: 'Last n Days',
      className: 'col-3',
      type: InputTypesEnum.Number,
      tab: 'base',
      validityChecker: TpEvaluatorSearchParams.validityForNumberOfDays,
      hide: (searchItem) =>
        searchItem.TypeOfExport !== 'PersonaExport' && searchItem.TypeOfExport !== 'SchedulingExport',
    },
    {
      fieldName: 'IncludeSelfDeactivated',
      label: 'Include Self Deactivated?',
      className: 'col-3',
      type: InputTypesEnum.EmptyYesNoSelect,
      tab: 'base',
      hide: (searchItem) =>
        !(searchItem.TypeOfExport === 'EmailCampaignExport' && !searchItem.HasEmailCampaignExportAdminRole) &&
        !(searchItem.TypeOfExport === 'TextMessageExport' && !searchItem.HasTextMessageExportAdminRole),
    },
    {
      fieldName: 'IncludeSelfDeactivatedPlaceholder',
      label: 'Include Self Deactivated?',
      className: 'col-3',
      type: InputTypesEnum.TextReadOnly,
      tab: 'base',
      hide: (searchItem) =>
        !(searchItem.TypeOfExport === 'EmailCampaignExport' && searchItem.HasEmailCampaignExportAdminRole) &&
        !(searchItem.TypeOfExport === 'TextMessageExport' && searchItem.HasTextMessageExportAdminRole),
    },
    // {
    //   fieldName: 'Agency (Do we need this field? It is not implemented in the db table.)',
    //   label: 'Agency?',
    //   className: 'col-3',
    //   type: InputTypesEnum.OnOff,
    //   tab: 'base',
    // },

    //------Personal tab
    {
      fieldName: 'Name',
      label: 'Name',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'personal',
    },
    {
      fieldName: 'Email',
      label: 'Email',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'personal',
    },
    {
      fieldName: 'AgeFrom',
      label: 'Age From',
      className: 'col-3',
      type: InputTypesEnum.Number,
      tab: 'personal',
    },
    {
      fieldName: 'AgeTo',
      label: 'Age To',
      className: 'col-3',
      type: InputTypesEnum.Number,
      tab: 'personal',
    },
    {
      fieldName: 'SelectedGender',
      label: 'Gender',
      className: 'col-3',
      type: InputTypesEnum.Select,
      options: TpEvaluatorSearchParams.getSelectOptionsForGender(),
      tab: 'personal',
    },
    {
      fieldName: 'ExcludeCitizenship',
      label: 'Exclude Citizenship Selection',
      className: 'col-4',
      type: InputTypesEnum.OnOff,
      tab: 'personal',
    },
    {
      fieldName: 'SelectedCitizenships',
      label: 'Citizenship',
      className: 'col-4',
      type: InputTypesEnum.LookupTextMultiChoice,
      options: 101,
      tab: 'personal',
    },
    //------Location tab
    {
      fieldName: 'ExcludeCountry',
      label: 'Exclude the Selected Countries',
      className: 'col-3',
      type: InputTypesEnum.OnOff,
      tab: 'location',
    },
    {
      fieldName: 'SelectedCountries',
      label: 'Country',
      className: 'col-3',
      type: InputTypesEnum.CountryNameMultiChoice,
      tab: 'location',
    },
    {
      fieldName: 'States',
      label: 'States, Regions (state1, region2, ...)',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'location',
      showCondition: (searchItem) => searchItem.SelectedCountries.length > 0 && !searchItem.ExcludeCountry,
    },
    {
      fieldName: 'ZipCodes',
      label: 'Zip Codes',
      className: 'col-3',
      type: InputTypesEnum.TextList,
      tooltipText: importListHelper.ListFieldTooltip,
      tab: 'location',
      showCondition: (searchItem) => searchItem.SelectedCountries.length > 0 && !searchItem.ExcludeCountry,
    },
    {
      fieldName: 'Cities',
      label: 'Cities (city1, city2, ...)',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'location',
      showCondition: (searchItem) => searchItem.SelectedCountries.length > 0 && !searchItem.ExcludeCountry,
    },
    {
      fieldName: 'Address',
      label: 'Address',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'location',
      showCondition: (searchItem) => searchItem.SelectedCountries.length > 0 && !searchItem.ExcludeCountry,
    },
    {
      fieldName: 'LocationSearchRadius',
      label: 'Location Search Radius',
      tooltipText:
        'For Country/State/Region/Zip Code/City input, even if multiple entries are provided, only the first entry will be considered for calculating the search radius.',
      className: 'col-3',
      type: InputTypesEnum.NumberWithTwoDecimalPlaces,
      tab: 'location',
      validityChecker: TpEvaluatorSearchParams.LocationRadiusSearchValidityChecker,
      showCondition: (searchItem) =>
        searchItem.SelectedCountries.length > 0 &&
        !searchItem.ExcludeCountry &&
        (searchItem.ZipCodes || searchItem.Cities),
    },
    {
      fieldName: 'LocationSearchRadiusMeasurementUnit',
      label: 'Location Search Radius Measurement Unit',
      className: 'col-3',
      type: InputTypesEnum.Select,
      options: TpEvaluatorSearchParams.getSelectOptionsForLocationSearchRadiusMeasurementUnit(),
      tab: 'location',
      showCondition: (searchItem) =>
        searchItem.SelectedCountries.length > 0 &&
        !searchItem.ExcludeCountry &&
        (searchItem.ZipCodes || searchItem.Cities),
    },
    //------Properties tab
    {
      fieldName: 'EvaluatorExtendedEvexRegBusiness',
      label: 'Owns a Registered Business',
      className: 'col-4',
      type: InputTypesEnum.MultiChoice,
      options: TpEvaluatorSearchParams.getOwnsRegBusinessOptions(),
      tab: 'properties',
    },
    {
      fieldName: 'SelectedBusinesses',
      label: 'Business Classification',
      className: 'col-4',
      type: InputTypesEnum.LookupMultiChoice,
      options: 99,
      showCondition: (searchItem) =>
        searchItem.EvaluatorExtendedEvexRegBusiness.length === 1 &&
        searchItem.EvaluatorExtendedEvexRegBusiness[0] === 'Yes',
      tab: 'properties',
    },

    //------Rating tab
    {
      fieldName: 'AverageRatingFrom',
      label: 'Average Rating From',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'rating',
    },
    {
      fieldName: 'AverageRatingTo',
      label: 'Average Rating To',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'rating',
    },
    {
      fieldName: 'LastRatingFrom',
      label: 'Last Rating From',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'rating',
    },
    {
      fieldName: 'LastRatingTo',
      label: 'Last Rating To',
      className: 'col-3',
      type: InputTypesEnum.Text,
      tab: 'rating',
    },

    //------Other tab
    {
      fieldName: 'UseSignUpDateRange',
      label: 'Use Sign-Up Date Range?',
      className: 'col-3',
      type: InputTypesEnum.OnOff,
      tab: 'other',
    },
    {
      fieldName: 'SignupDateTimeUtcFrom',
      label: 'Signup Date Time Utc From',
      className: 'col-3',
      showCondition: (searchItem) => searchItem.UseSignUpDateRange,
      type: InputTypesEnum.DateWithIndefinite,
      tab: 'other',
    },
    {
      fieldName: 'SignupDateTimeUtcTo',
      label: 'Signup Date Time Utc To',
      className: 'col-3',
      showCondition: (searchItem) => searchItem.UseSignUpDateRange,
      type: InputTypesEnum.DateWithIndefinite,
      tab: 'other',
    },
    {
      fieldName: 'EmailOptOut',
      label: 'Opted out from emails?',
      className: 'col-3',
      type: InputTypesEnum.EmptyYesNoSelect,
      tab: 'other',
      hide: (searchItem) =>
        searchItem.TypeOfExport === 'EmailCampaignExport' && !searchItem.HasEmailCampaignExportAdminEmailOptOutRole,
    },
    {
      fieldName: 'EmailOptOutPlaceholder',
      label: 'Opted out from emails?',
      className: 'col-3',
      type: InputTypesEnum.TextReadOnly,
      tab: 'other',
      hide: (searchItem) =>
        searchItem.TypeOfExport !== 'EmailCampaignExport' || searchItem.HasEmailCampaignExportAdminEmailOptOutRole,
    },
    {
      fieldName: 'EmailVerified',
      label: 'Email Verified?',
      className: 'col-3',
      type: InputTypesEnum.EmptyYesNoSelect,
      tab: 'other',
      hide: (searchItem) => searchItem.TypeOfExport === 'EmailCampaignExport',
    },
    {
      fieldName: 'EmailVerifiedPlaceholder',
      label: 'Email Verified?',
      className: 'col-3',
      type: InputTypesEnum.TextReadOnly,
      tab: 'other',
      hide: (searchItem) => searchItem.TypeOfExport !== 'EmailCampaignExport',
    },
    {
      fieldName: 'TextAllowPlaceholder',
      label: 'Text Message Authorized?',
      className: 'col-3',
      type: InputTypesEnum.TextReadOnly,
      tab: 'other',
      hide: (searchItem) => searchItem.TypeOfExport !== 'TextMessageExport',
    },
    {
      fieldName: 'IncludeNATextMessageAuthorized',
      label: 'Include N/A Text Message Authorized?',
      className: 'col-3',
      type: InputTypesEnum.EmptyYesNoSelect,
      tab: 'other',
      hide: (searchItem) =>
        !searchItem.HasTextMessageExportAdminRole || searchItem.TypeOfExport !== 'TextMessageExport',
    },
    {
      fieldName: 'IncludeNATextMessageAuthorizedPlaceholder',
      label: 'Include N/A Text Message Authorized?',
      className: 'col-3',
      type: InputTypesEnum.TextReadOnly,
      tab: 'other',
      hide: (searchItem) => searchItem.HasTextMessageExportAdminRole || searchItem.TypeOfExport !== 'TextMessageExport',
    },
  ];

  static iconName = 'fa-solid fa-search';
  static saveType = 'TpEvaluatorSearchParams';

  static getUserOffice() {
    const user = AuthService.getInstance().GetLoggedInUser();
    return user.officeId;
  }

  static ConvertToJSON(originalSearchItem) {
    const correctedSearchItem = { ...originalSearchItem };

    for (const prop in correctedSearchItem) {
      correctedSearchItem[prop] = correctedSearchItem[prop].toString();
    }
    return JSON.stringify(correctedSearchItem);
  }

  static getSelectOptionsForStatus() {
    return [
      { label: 'Choose one...', value: '', disabled: false },
      { label: 'Contact', value: 'Contact', disabled: false },
      { label: 'Deleted', value: 'Deleted', disabled: false },
      { label: 'DoNotContact', value: 'DoNotContact', disabled: false },
      { label: 'SelfDeactivated', value: 'SelfDeactivated', disabled: false },
    ];
  }
  static getSelectOptionsForThirdPartyStatus() {
    return [
      { label: 'Choose one...', value: '', disabled: false },
      { label: 'Active', value: 'Active', disabled: false },
      { label: 'Hire', value: 'Hire', disabled: false },
      { label: 'Do Not Hire', value: 'DoNotHire', disabled: false },
      { label: 'Deleted', value: 'Deleted', disabled: false },
      { label: 'Public deactivated', value: 'Public deactivated', disabled: false },
      { label: 'Secret & Self-deactivated', value: 'Secret & Self-deactivated', disabled: false },
      { label: 'Secret deactivated', value: 'Secret deactivated', disabled: false },
      { label: 'Self-deactivated', value: 'Self-deactivated', disabled: false },
    ];
  }

  static getSelectOptionsForDeactivationType() {
    return [
      { label: 'Choose one...', value: '', disabled: false },
      { label: 'Public', value: 'Public', disabled: false },
      { label: 'Secret', value: 'Secret', disabled: false },
      { label: 'Secret & Self', value: 'Secret and Self', disabled: false },
      { label: 'Self', value: 'Self', disabled: false },
    ];
  }

  static getSelectOptionsForGender() {
    return [
      { label: 'Choose one...', value: '', disabled: false },
      { label: 'Male', value: 'Male', disabled: false },
      { label: 'Female', value: 'Female', disabled: false },
      { label: 'Other', value: 'Other', disabled: false },
    ];
  }

  static getOwnsRegBusinessOptions() {
    return [
      { label: 'Choose one...', value: '', disabled: false },
      { label: 'No Response', value: '', disabled: false },
      { label: 'Yes', value: 'Yes', disabled: false },
      { label: 'No', value: 'No', disabled: false },
      { label: 'No Information', value: '', disabled: false },
    ];
  }

  static validityForNumberOfDays(e, item) {
    if (e < 0) return 'The number of days cannot be negative.';
    if (e > 1825) return 'The number of days cannot be more than 5 years (1825 days).';
    return '';
  }

  static getSelectOptionsForLocationSearchRadiusMeasurementUnit() {
    return [
      { label: 'Kilometers', value: 'kilometers', disabled: false },
      { label: 'Miles', value: 'miles', disabled: false },
    ];
  }

  static LocationRadiusSearchValidityChecker(value) {
    if (value === '') return '';
    return value >= 1 && value <= 100
      ? ''
      : 'Location Search Radius must be between 1 and 100 units of any measurement.';
  }
}
